import { ResponsiveLine } from "@nivo/line";
import { useTranslation } from "react-i18next";

import InfoIcon from "assets/icons/info.svg?react";

import { chartSmallConfig } from "config/chart";

import {
  convertValueToLocale,
  convertValueToLocaleForm,
} from "helpers/convertValueToLocale";

import {
  getChartData,
  getEvenlySpacedYears,
} from "./PropertyValueCard.helpers";

import { Popover } from "shared/ui/components/Popover";
import { Typography } from "shared/ui/components/Typography";

import styles from "./PropertyValueCard.module.scss";

const _renderInfoPopover = ({ t, info }: { t: any; info: any }) => {
  return (
    <div className={styles.popover}>
      <Typography variant="bodyS" weight="light">
        {t("calculator_pro_page.property_value_chart.info_popover_title")}
      </Typography>
      {info.apartmentsPrices.map((item: any, index: number) => (
        <div key={index}>
          <div className={styles.popoverInfoItem}>
            <Typography
              variant="bodyS"
              weight="semiBold"
              className={styles.popoverInfoItemTitle}
            >
              {t(
                `calculator_pro_page.property_value_chart.info.apt_info_popover_title`
              )}
            </Typography>
            <Typography
              variant="bodyS"
              weight="light"
              className={styles.popoverInfoItemValue}
            >
              {convertValueToLocale(item.aptPrice)}
            </Typography>
          </div>
          {item.aptFurniturePrice > 0 && (
            <div className={styles.popoverInfoItem}>
              <Typography
                variant="bodyS"
                weight="semiBold"
                className={styles.popoverInfoItemTitle}
              >
                {t(
                  `calculator_pro_page.property_value_chart.info.furniture_info_popover_title`
                )}
              </Typography>
              <Typography
                variant="bodyS"
                weight="light"
                className={styles.popoverInfoItemValue}
              >
                {convertValueToLocale(item.aptFurniturePrice)}
              </Typography>
            </div>
          )}
        </div>
      ))}
      {info.parkingsPrices.map((item: any, index: number) => (
        <div key={index} className={styles.popoverInfoItem}>
          <Typography
            variant="bodyS"
            weight="semiBold"
            className={styles.popoverInfoItemTitle}
          >
            {t(
              `calculator_pro_page.property_value_chart.info.parking_info_popover_title`
            )}
          </Typography>
          <Typography
            variant="bodyS"
            weight="light"
            className={styles.popoverInfoItemValue}
          >
            {convertValueToLocale(item.parkingPrice)}
          </Typography>
        </div>
      ))}
      <div className={styles.popoverInfoItem}>
        <Typography variant="bodyS" weight="light">
          {t(
            "calculator_pro_page.property_value_chart.info.info_popover_subtitle"
          )}
        </Typography>
      </div>
      {info.apartmentsPrices.map((item: any, index: number) => (
        <div key={index} className={styles.popoverInfoItem}>
          <Typography
            variant="bodyS"
            weight="semiBold"
            className={styles.popoverInfoItemTitle}
          >
            {t(
              `calculator_pro_page.property_value_chart.info.apt_info_popover_rate_title`
            )}
          </Typography>
          <Typography
            variant="bodyS"
            weight="light"
            className={styles.popoverInfoItemValue}
          >
            {t(
              "calculator_pro_page.property_value_chart.info.apt_info_popover_rate_value",
              {
                aptPriceGrowth: item.aptPriceGrowth,
              }
            )}
          </Typography>
        </div>
      ))}
      {info.parkingsPrices.map((item: any, index: number) => (
        <div key={index} className={styles.popoverInfoItem}>
          <Typography
            variant="bodyS"
            weight="semiBold"
            className={styles.popoverInfoItemTitle}
          >
            {t(
              `calculator_pro_page.property_value_chart.info.parking_info_popover_rate_title`
            )}
          </Typography>
          <Typography
            variant="bodyS"
            weight="light"
            className={styles.popoverInfoItemValue}
          >
            {t(
              "calculator_pro_page.property_value_chart.info.parking_info_popover_rate_value",
              {
                parkingPriceGrowth: item.parkingPriceGrowth,
              }
            )}
          </Typography>
        </div>
      ))}
    </div>
  );
};

const CustomTooltip = ({ point }: { point: any }) => {
  const { data } = point;

  const year = data.x;
  const valueFormatted = convertValueToLocale(data.y);

  return (
    <div className={styles.tooltip}>
      <Typography variant="bodyS" weight="semiBold">
        {year}: {valueFormatted}
      </Typography>
    </div>
  );
};

const PropertyValueCard = ({
  propertyValueChart,
  activePosition,
}: {
  propertyValueChart: any;
  activePosition: 10 | 20 | 30;
}) => {
  const { t } = useTranslation();

  const chartData = getChartData(propertyValueChart, activePosition);
  const yearsToShow = getEvenlySpacedYears(
    propertyValueChart,
    activePosition,
    6
  );

  return (
    <div className={styles.chartItem}>
      <div className={styles.header}>
        <Typography
          className={styles.chartTitle}
          variant="bodyS"
          weight="semiBold"
        >
          {t("calculator_pro_page.property_value_chart.title")}
        </Typography>
        <Popover
          placement="top"
          triggerType="hover"
          yOffset={15}
          floatingArrowProps={{ width: 17, height: 10, fill: "#fff" }}
          triggerClassName={styles.trigger}
          triggerElement={() => <InfoIcon className={styles.infoIcon} />}
        >
          {_renderInfoPopover({ t, info: propertyValueChart.info })}
        </Popover>
      </div>
      <Typography
        className={styles.chartTitlePositive}
        variant="bodyM"
        weight="semiBold"
      >
        {convertValueToLocale(propertyValueChart.propertyValue[activePosition])}
      </Typography>

      <Typography variant="bodyXS" className={styles.chartDescription}>
        {t("calculator_pro_page.property_value_chart.description", {
          propertyValueProfit: convertValueToLocale(
            propertyValueChart.propertyValueProfit[activePosition]
          ),
          weightedPriceGrowthRate: convertValueToLocaleForm(
            propertyValueChart.weightedPriceGrowthRate
          ),
          dynamic: convertValueToLocaleForm(
            propertyValueChart.weightedPriceGrowthDynamic
          ),
        })}
      </Typography>

      <div className={styles.chart}>
        <ResponsiveLine
          {...chartSmallConfig}
          data={[
            {
              id: "chart",
              data: chartData,
            },
          ]}
          yScale={{
            type: "linear",
            min: "auto",
            max: "auto",
            stacked: false,
            reverse: false,
          }}
          fill={[
            {
              id: "gradient-green",
              match: { id: "chart" },
            },
          ]}
          axisLeft={{
            tickSize: 0,
            tickPadding: 5,
            tickRotation: 0,
            format: () => "",
          }}
          axisBottom={{
            format: (year: number) => {
              return yearsToShow.includes(year) ? year : "";
            },
          }}
          enableTouchCrosshair={true}
          useMesh={true}
          tooltip={CustomTooltip}
        />
      </div>
    </div>
  );
};

export default PropertyValueCard;
